import type { IMapEngine } from '../types';
import type { EventHandler } from '../types/map-engine';
import { readPointCoordinates } from '../geometry';
import { MapEventBus } from './MapEventBus';

/**
 * 地图事件管理器
 *
 * 层级：map-core（"地图怎么动"）
 *
 * 职责：
 * 1. 统一封装 BMapGL 原生事件（click / dblclick / mousemove / moveend / zoomend）的注册与注销
 * 2. 将原生事件对象转换为类型安全的应用事件，并通过 MapEventBus 分发
 * 3. 提供 `on` / `off` / `clear` 接口，供 controller / interaction 订阅应用事件
 *
 * 不关心：业务如何处理事件、编辑器模式切换、React 生命周期
 */
export class MapEventManager {
  private nativeHandlers = new Map<string, EventHandler>();

  constructor(
    private engine: IMapEngine,
    private bus: MapEventBus
  ) {}

  /**
   * 开始监听所有地图原生事件。
   * 应在地图初始化完成后调用一次。
   */
  bind() {
    const events = ['click', 'dblclick', 'mousemove', 'moveend', 'zoomend'] as const;
    events.forEach((event) => {
      const handler = (e: any) => this.handleNativeEvent(event, e);
      this.engine.on(event, handler);
      this.nativeHandlers.set(event, handler);
    });
  }

  /**
   * 取消所有原生事件监听并清空应用事件总线。
   * 应在地图销毁前调用。
   */
  clear() {
    this.nativeHandlers.forEach((handler, event) => {
      this.engine.off(event, handler);
    });
    this.nativeHandlers.clear();
    this.bus.clear();
  }

  /** 订阅应用事件 */
  on(event: string, handler: EventHandler) {
    return this.bus.on(event, handler as any);
  }

  /** 取消订阅应用事件 */
  off(event: string, handler: EventHandler) {
    this.bus.off(event, handler as any);
  }

  /** 手动触发应用事件（供覆盖物、绘制等模块使用） */
  emit(event: string, payload?: any) {
    this.bus.emit(event, payload);
  }

  private handleNativeEvent(
    event: string,
    e: any
  ) {
    const payload = this.transformPayload(event, e);
    this.bus.emit(`map:${event}`, payload);
  }

  private transformPayload(event: string, e: any) {
    switch (event) {
      case 'click':
      case 'dblclick':
      case 'mousemove': {
        const roughLngLat = readPointCoordinates(e?.latlng);
        // 高 zoom 下 e.latlng 有 float32 精度损失（zoom 25 实测偏移 ~14px），
        // 以 e.pixel 为基准精化；e.pixel 缺失时退回原始值
        const lngLat = e?.pixel
          ? this.engine.refineEventLngLat(roughLngLat, e.pixel)
          : roughLngLat;
        const pixel = e?.pixel ?? { x: 0, y: 0 };
        return { lngLat, pixel, domEvent: e?.domEvent };
      }
      case 'moveend': {
        return { center: this.engine.getCenter() };
      }
      case 'zoomend': {
        return { zoom: this.engine.getZoom() };
      }
      default:
        return e;
    }
  }
}
